import React, { useEffect } from 'react';
import Joyride, { CallBackProps, TooltipRenderProps } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setRun, setStartTour, setStepIndex } from '../../Store/Actions/joyrideActions';
import { Button } from '../Utility/Button';

interface JoyrideWrapperProps {
    children: React.ReactNode;
}

export const JoyrideWrapper: React.FC<JoyrideWrapperProps> = ({ children }) => {
    const stepIndex = useSelector((state: any) => state.joyride.stepIndex);
    const run = useSelector((state: any) => state.joyride.run);
    const startTour = useSelector((state: any) => state.joyride.startTour);
    const i18n = useSelector((state: any) => state.i18n.i18n);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const windowWidth = useSelector((state: any) => state.environment.windowWidth);
    const breakpointXL = getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-xl').trim();
    if (startTour === true && windowWidth >= breakpointXL) {
        if (location.pathname !== '/start') {
            navigate('/start');
        }
        dispatch(setRun(true));
        dispatch(setStepIndex(0));
        dispatch(setStartTour(false));
    }

    useEffect(() => {
        if (windowWidth < breakpointXL) {
            dispatch(setRun(false));
        }
    }, [windowWidth, dispatch, breakpointXL]);

    function CustomTooltip(props: TooltipRenderProps) {
        const { backProps, continuous, index, primaryProps, skipProps, step, tooltipProps } = props;

        return (
            <div
                className="tooltip__body sm:max-w-[500px] bg-joyride-backgroundColor p-6 rounded-xl shadow-lg flex flex-col gap-8"
                {...tooltipProps}
            >
                {step.title && <h3 className="tooltip__title text-joyride-headlineColor !m-0 !p-0">{step.title}</h3>}

                <div className="tooltip__content text-joyride-textColor mb-4">{step.content}</div>

                <div className="tooltip__footer flex justify-between">
                    <Button variant={'secondary'} className="tooltip__button" {...skipProps}>
                        {skipProps.title}
                    </Button>
                    <div className="tooltip__spacer flex gap-4">
                        {index > 0 && (
                            <Button variant={'secondary'} className="tooltip__button" {...backProps}>
                                {backProps.title}
                            </Button>
                        )}
                        {continuous && (
                            <Button className="tooltip__button tooltip__button--primary" {...primaryProps}>
                                {primaryProps.title}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const steps: any = [
        {
            target: 'body',
            content: (
                <div>
                    <p>
                        {i18n.translate({
                            id: 'step-start.text',
                            params: { title: document.title },
                            sourceName: 'Joyride',
                        })}
                    </p>
                </div>
            ),
            title: i18n.translate({ id: 'step-start.headline', sourceName: 'Joyride' }),
            disableBeacon: true,
            placement: 'center',
        },
        {
            target: '.joyride-step-chat-input',
            content: i18n.translate({ id: 'step-chatInput', sourceName: 'Joyride' }),
            disableBeacon: true,
            placement: 'top',
        },
        {
            target: '.joyride-step-chat-fileUpload',
            content: i18n.translate({ id: 'step-fileUpload', sourceName: 'Joyride' }),
            disableBeacon: true,
            placement: 'top',
        },
        {
            target: '.joyride-step-chat-list',
            content: i18n.translate({ id: 'step-chatList', sourceName: 'Joyride' }),
            disableBeacon: true,
            placement: 'right',
        },
        {
            target: '.joyride-step-navigation-userprompts',
            content: i18n.translate({ id: 'step-assistentList', sourceName: 'Joyride' }),
            disableBeacon: true,
            placement: 'right',
        },
    ];

    const handleCallback = (data: CallBackProps) => {
        const { type, index, action } = data;

        if (type === 'error:target_not_found') {
            dispatch(setStepIndex(index + (action === 'prev' ? -1 : 1)));
        } else if (type === 'tour:start') {
            localStorage.setItem('tourStart', 'false');
        } else if (type === 'step:after') {
            if (action === 'close') {
                dispatch(setRun(false));
                if (location.pathname !== '/chats') {
                    navigate('/chats');
                }
            } else if (action === 'prev') {
                dispatch(setStepIndex(index - 1));
            } else if (action === 'next') {
                dispatch(setStepIndex(index + 1));
            }
        } else if (type === 'tour:end') {
            dispatch(setRun(false));
        }
    };

    return (
        <>
            <div className="react-joyride__tooltip hidden __floater__arrow"></div>
            <Joyride
                tooltipComponent={CustomTooltip}
                callback={handleCallback}
                locale={{
                    back: i18n.translate({ id: 'back', sourceName: 'Joyride' }),
                    close: i18n.translate({ id: 'close', sourceName: 'Joyride' }),
                    last: i18n.translate({ id: 'last', sourceName: 'Joyride' }),
                    next: i18n.translate({ id: 'next', sourceName: 'Joyride' }),
                    open: i18n.translate({ id: 'open', sourceName: 'Joyride' }),
                    skip: i18n.translate({ id: 'skip', sourceName: 'Joyride' }),
                }}
                hideCloseButton
                continuous
                spotlightPadding={5}
                showSkipButton
                styles={{
                    options: {
                        overlayColor: 'rgba(0, 0, 0, 0.4)',
                        primaryColor: '#00C9B2',
                        width: 500,
                    },
                }}
                stepIndex={stepIndex}
                steps={steps}
                run={run}
            />
            {children}
        </>
    );
};
